// eslint-disable-next-line
import React, { useState, useEffect, useRef, Fragment } from "react";
import { getQRCode } from "../../API/api.jsx";
import { CiLock } from "react-icons/ci";
import { FaWhatsapp } from "react-icons/fa";
import QRCodeComponent from "./QRCodeComponent";
import Countdown from "react-countdown";
import UsageGuidelinesModal from "./UsageGuidelinesModal.jsx";
import "./login.css";

export const Login = () => {
    // ✅ Configurable constants
    const MAX_ATTEMPTS = 8;
    const QR_LIFESPAN_MS = 120000; // 2 minutes
    const POLL_INTERVAL_MS = 15000; // 15 seconds

    const [qrcode, setQrcode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [attemptCount, setAttemptCount] = useState(0);
    const [openTime, setOpenTime] = useState(Date.now() + QR_LIFESPAN_MS);
    const [expired, setExpired] = useState(false);
    const [showGuidelines, setShowGuidelines] = useState(false);
    const intervalRef = useRef(null);

    const fetchQRCode = async (token) => {
        try {
            setLoading(true);
            const response = await getQRCode(token);
            setQrcode(response.data);
            setLoading(false);
        } catch {
            setQrcode(null);
            setLoading(false);
            clearInterval(intervalRef.current);
        } finally {
            setAttemptCount((prev) => prev + 1);
        }
    };

    const startQRCodeProcess = (token) => {
        setExpired(false);
        setAttemptCount(0);
        setOpenTime(Date.now() + QR_LIFESPAN_MS);
        fetchQRCode(token);

        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setAttemptCount((prev) => {
                if (prev >= MAX_ATTEMPTS - 1) {
                    clearInterval(intervalRef.current);
                    setExpired(true);
                    return prev;
                }
                fetchQRCode(token);
                return prev + 1;
            });
        }, POLL_INTERVAL_MS);
    };

    useEffect(() => {
        const path = window.location.pathname;
        const token = path.substring(path.lastIndexOf("/") + 1);

        if (!token) {
            setQrcode(null);
            setLoading(false);
            return;
        }

        startQRCodeProcess(token);
        return () => clearInterval(intervalRef.current);
    }, []);

    const countdownRenderer = ({ minutes, seconds, completed }) => {
        if (completed) {
            setExpired(true);
            clearInterval(intervalRef.current);
            return <span className="countdown-display">00:00</span>;
        }
        return (
            <span className="countdown-display">
                {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </span>
        );
    };

    const handleRefresh = () => {
        const token = window.location.pathname.substring(
            window.location.pathname.lastIndexOf("/") + 1
        );
        startQRCodeProcess(token);
    };

    return (
        <Fragment>
            {/* ✅ Guidelines Modal */}
            <UsageGuidelinesModal
                isOpen={showGuidelines}
                onClose={() => setShowGuidelines(false)}
            />

            <div className="qr-login-container">
                <header className="qr-login-header">
                    <div className="logo-brand">
                        <FaWhatsapp className="brand-icon" />
                        <h1>mySMSGatewayWhatsApp</h1>
                    </div>

                </header>

                <main className="qr-login-main">
                    <div className="qr-login-card">
                        <div className="card-top-section">
                            <h2>Login into mySMSGatewayWhatsApp</h2>
                            <div className="connection-status">
                                <div className="status-text">
                                    Please note it can take up to two minutes for your QR code to
                                    appear.
                                    <Countdown date={openTime} renderer={countdownRenderer} />
                                </div>
                                <div className="attempt-counter">Processing: {attemptCount}</div>
                            </div>
                        </div>

                        <div className="qr-content-area">
                            <section className="qr-code-panel">
                                <div className="qr-code-container">
                                    <QRCodeComponent loading={loading} qrcode={qrcode} />
                                    {loading && (
                                        <div className="qr-loading-overlay">
                                            <div className="loading-spinner"></div>
                                        </div>
                                    )}
                                </div>
                                {expired ? (
                                    <button className="qr-refresh-btn" onClick={handleRefresh}>
                                        QR Expired — Refresh
                                    </button>
                                ) : (
                                    <p className="qr-instruction">Scan the QR code to login</p>
                                )}
                            </section>

                            <section className="instructions-panel">
                                <h3>Use WhatsApp on your computer</h3>
                                <p>Scan this QR code with your phone to log in:</p>
                                <ul className="steps-guide" style={{ listStyle: "none" }}>
                                    <li>Open WhatsApp on your phone.</li>
                                    <li>
                                        Tap <strong>Menu</strong> or <strong>Settings</strong> and
                                        select <strong>Linked Devices</strong>.
                                    </li>
                                    <li>
                                        Point your phone's camera at this screen to scan the QR code.
                                    </li>
                                    <li>
                                        If you haven't read them yet, please review the{" "}
                                        <button
                                            className="guidelines-btn"
                                            onClick={() => setShowGuidelines(true)}
                                        >
                                            WhatsApp Usage Guidelines
                                        </button>
                                        .
                                    </li>

                                </ul>
                            </section>
                        </div>

                        <div className="security-footer">
                            <CiLock className="security-icon" />
                            <span>Your personal messages are end-to-end encrypted</span>
                        </div>
                    </div>
                </main>
            </div>
        </Fragment>
    );
};
